'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Navbar from '../../Navbar';
import VideoModal from '../Modals/VideoModal';
import styles from './styles/HeroSection.module.css';

export default function HeroSection({
  course,
  user,
  teacher,
  teacherAvatarUrl = '/images/demo-profile.png',
  onEditHeroSection,
}) {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const getHeroImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;

    if (imageUrl.includes('/card/') && imageUrl.includes('_250x200.webp')) {
      return imageUrl
        .replace('/card/', '/hero/')
        .replace('_250x200.webp', '_500xauto.webp');
    }

    return imageUrl;
  };

  const heroImageUrl = getHeroImageUrl(course?.imageUrl);
  const teacherData = Array.isArray(teacher) ? teacher[0] : teacher;
  const canEdit =
    user?.role === 'ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    (user?.role === 'TEACHER' && teacherData?.id === user?.id);

  return (
    <>
      <header className={styles.header}>
        <Navbar />
      </header>

      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.heroBanner}>
            {/* LAYER 1: blurred fill background */}
            {heroImageUrl ? (
              <img
                src={heroImageUrl}
                alt=""
                aria-hidden="true"
                className={styles.bgBlur}
                onError={(e) => {
                  const fallback = course?.imageUrl;
                  if (fallback && e.currentTarget.src !== fallback) {
                    e.currentTarget.src = fallback;
                  }
                }}
              />
            ) : null}

            {/* LEFT-heavy overlay for readable text */}
            <div className={styles.overlay} />

            {/* LAYER 2: right-side framed image (play button MUST live in here) */}
            {heroImageUrl ? (
              <div className={styles.rightMedia}>
                <img
                  src={heroImageUrl}
                  alt={course?.name || 'Course image'}
                  className={styles.heroImage}
                  onError={(e) => {
                    const fallback = course?.imageUrl;
                    if (fallback && e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                    }
                  }}
                />

                {!!course?.introVidUrl && (
                  <button
                    type="button"
                    className={styles.playButton}
                    onClick={() => setIsVideoOpen(true)}
                    aria-label="Play intro video"
                  >
                    <svg
                      width="72"
                      height="72"
                      viewBox="0 0 72 72"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={styles.playIcon}
                    >
                      <circle
                        cx="36"
                        cy="36"
                        r="35"
                        fill="black"
                        fillOpacity="0.65"
                      />
                      <path d="M28 22L50 36L28 50V22Z" fill="white" />
                    </svg>
                  </button>
                )}
              </div>
            ) : null}

            {/* Content */}
            <div className={styles.heroInner}>
              <div className={styles.heroText}>
                <h1 className={styles.title}>{course?.name}</h1>
                <p className={styles.subtitle}>{course?.description}</p>

                {teacherData ? (
                  <div className={styles.profileRow}>
                    <Link
                      href={`/teacher-profile/${teacherData.id}`}
                      className={styles.profileLink}
                    >
                      <div className={styles.profileAvatarWrap}>
                        <img
                          src={teacherAvatarUrl}
                          alt="Teacher Profile Picture"
                          className={styles.profileAvatar}
                        />
                      </div>

                      <div className={styles.profileText}>
                        <div className={styles.profileName}>
                          {teacherData?.name}
                        </div>
                      </div>
                    </Link>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className={styles.btnSection}>
            {canEdit ? (
              <button
                type="button"
                className={`${styles.btn} ${styles.editBtn}`}
                onClick={onEditHeroSection}
              >
                Edit Hero Section
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        content={course?.introVidUrl}
      />
    </>
  );
}

function OrganizationInfo() {
  return (
    <div className={styles.enrollOrgRow}>
      <div className={styles.orgInfo}>
        <Link href="/organization" className={styles.orgLink}>
          <Image
            src="/images/penguinlearn-logo.png"
            alt="Organization Logo"
            width={50}
            height={50}
            className={styles.orgLogo}
            quality={95}
          />
          <div className={styles.orgText}>
            <h2 className={styles.orgName}>PenguinLearn</h2>
            {/*
            <div className={styles.courseRating}>
              <span>⭐⭐⭐⭐⭐</span>
              <small>4.9 (165 reviews)</small>
            </div>
            */}
          </div>
        </Link>
      </div>
    </div>
  );
}

function ProfileInfo({ teacher, avatarUrl }) {
  const teacherData = Array.isArray(teacher) ? teacher[0] : teacher;
  if (!teacherData) return null;

  return (
    <div className={styles.enrollOrgRow}>
      <div className={styles.orgInfo}>
        <Link
          href={`/teacher-profile/${teacherData.id}`}
          className={styles.orgLink}
        >
          <div className={styles.avatarRing}>
            <img
              src={avatarUrl || '/images/demo-profile.svg'}
              alt="Teacher Profile Picture"
              className={styles.avatarImg}
            />
          </div>
          <div className={styles.orgText}>
            <h2 className={styles.orgName}>{teacherData.name}</h2>
          </div>
        </Link>
      </div>
    </div>
  );
}